import { cva } from "class-variance-authority";

import {
  FloatingLabelField as BaseFloatingLabelField,
  type FloatingLabelFieldProps as BaseFloatingLabelFieldProps,
} from "@/components/ui/floating-label-field";
import { useFormField } from "@/components/ui/form";
import { cn } from "@/lib/utils";

const ulThemeFloatingLabelFieldVariants = cva(
  [
    // Layout & Spacing
    "mb-2",

    // Base Background & Colors
    "theme-universal:bg-input-bg",
    "theme-universal:text-input-text",

    // Border Styling
    "theme-universal:border-(length:--ul-theme-border-input-border-weight)",
    "theme-universal:border-input-border",

    // Border Radius — rektangulære hjørner per Thon Hotels designsystem
    "rounded-none",
    "theme-universal:rounded-input",

    // Typography - Input Text
    "theme-universal:text-(length:--ul-theme-font-body-text-size)",
    "theme-universal:font-body-text",

    // Placeholder Styling
    "theme-universal:placeholder:text-input-labels",
    "theme-universal:[&_input]:placeholder:text-input-labels",
    "theme-universal:placeholder:text-(length:--ul-theme-font-input-labels-size)",
    "theme-universal:[&_input]:placeholder:text-(length:--ul-theme-font-input-labels-size)",
    "theme-universal:placeholder:font-input-label",
    "theme-universal:[&_input]:placeholder:font-input-label",

    // Floating Label Background
    "theme-universal:[&_label]:bg-input-bg",

    // Floating Label - Base Styling (applies to all labels)
    "theme-universal:[&_label]:text-input-labels",
    "theme-universal:[&_label]:text-(length:--ul-theme-font-input-labels-size)",
    "theme-universal:[&_label]:font-input-label",
  ],
  {
    variants: {
      themeState: {
        default: [
          // Focus States - Default. Border color alone signals focus; no
          // separate ring - any nonzero ring width (1px, 1.5px, 2px all
          // tested) produces a visible anti-aliasing seam against the
          // border at some zoom level, since they're two separately
          // rendered lines (border vs box-shadow) on the same edge.
          "theme-universal:focus-within:border-base-focus",
          // Unscoped: cancels the base FloatingLabelField's own default
          // variant, which applies focus-within:ring-4 focus-within:ring-ring.
          // tailwind-merge doesn't recognize the theme-universal-scoped
          // classes as conflicting with those (different modifier stack), so
          // without this the base component's ring would still render.
          "focus-within:ring-0",

          // Focus Label States - Override label color when focused
          "theme-universal:focus-within:[&_label]:text-base-focus",
        ],
        error: [
          // Error States - border turns red, but the input value and
          // label text stay the normal (black) color rather than red too.
          "theme-universal:border-error",
          "theme-universal:focus-within:border-error",
          // Unscoped - see comment in the default variant above.
          "focus-within:ring-0",
          // The base FloatingLabelField's own error variant sets
          // text-destructive-foreground on the wrapper (which the input
          // inherits) and on the label directly - these unscoped overrides
          // win over that via tailwind-merge (see comment above) to keep
          // the text black instead.
          "text-input-text",
          "[&_label]:text-input-labels",
        ],
      },
    },
    defaultVariants: {
      themeState: "default",
    },
  },
);

export interface ULThemeFloatingLabelFieldProps extends BaseFloatingLabelFieldProps {
  /**
   * Additional wrapper class for form field container
   */
  wrapperClassName?: string;
}

function ULThemeFloatingLabelField({
  className,
  variant = "default",
  size = "default",
  wrapperClassName,
  error,
  "aria-describedby": ariaDescribedBy,
  ...props
}: ULThemeFloatingLabelFieldProps) {
  // Get the form field context for proper ID association
  const { formItemId, formMessageId } = useFormField();

  // Tie the input to its error message (rendered by ULThemeFormMessage with
  // id={formMessageId}) so screen readers announce it on revisiting the field.
  const describedBy =
    [ariaDescribedBy, error ? formMessageId : undefined].filter(Boolean).join(" ") || undefined;

  // Determine variant based on error prop
  const effectiveVariant = error ? "error" : variant;

  // Generate theme classes using CVA - automatically use error state when error prop is true
  const themeOverrides = ulThemeFloatingLabelFieldVariants({
    themeState: error ? "error" : "default",
  });

  return (
    <div className={cn("w-full", wrapperClassName)}>
      <BaseFloatingLabelField
        id={formItemId}
        className={cn(themeOverrides, className)}
        variant={effectiveVariant}
        size={size}
        error={error}
        aria-invalid={error || undefined}
        aria-describedby={describedBy}
        labelClassName="theme-universal:font-bold theme-universal:text-body-text theme-universal:text-(length:--ul-theme-font-body-text-size)"
        {...props}
      />
    </div>
  );
}

ULThemeFloatingLabelField.displayName = "ULThemeFloatingLabelField";

export { ULThemeFloatingLabelField };
